import { useEffect, useRef, useState } from 'react';
import { assetsMap } from '@flobi/artwork-core';
import { artworkCmd } from '../../services/apiClient';
import type { MediaKind, Model } from './contracts';
import { TOOLS, type ToolDef, type ToolId } from '../imageTools/catalogue';
import { requestPictureTool } from '../imageTools/request';
import { askLearned } from './startingModel';

/**
 * Artwork's Generate panel, with Atlas (Taste) and Jev (typed decisions) behind it.
 *
 *   learned       the frame Atlas is SURE of for new work in this file (a brand file: the brand's;
 *                 artwork.ai.learned); its model is the panel's starting model (startingModel.ts)
 *   understand    what the words ask for, read as the panel's own settings (artwork.ai.understand)
 *   reportTaste   a choice made on the canvas about generated pictures (artwork.ai.taste)
 *
 * None of it ever stops the panel: a slow or missing answer is no answer, and nothing the person
 * set themselves is ever changed — a suggestion is one press, and only ever one at a time.
 */

type Band = 'sure' | 'offer' | 'ask';
interface Decided<T> { value: T; band: Band; confidence: number }
interface Understood { kind: Decided<MediaKind> | null; shape: Decided<string> | null; model: Decided<string> | null; lettering: Decided<boolean> | null; tool: Decided<ToolId> | null }

/** Models that set readable words well, best first, for a prompt that asks for lettering. */
const LETTERING = [/ideogram/i, /gpt-image/i, /recraft/i, /nano-banana-pro/i, /seedream/i, /flux-2-pro/i];
const goodAtLettering = (id: string) => LETTERING.some((re) => re.test(id));

/** The words as typed, without the reference tags (`@[ref:…:name]` → name). */
const plain = (prompt: string) => prompt.replace(/@\[[^\]]*?:([^\]:]+)\]/g, '$1').replace(/\s+/g, ' ').trim();

/** The one picture or clip chosen on the page, when the panel has exactly that (a shape painted with a picture counts). */
export function selectedPicture(node: unknown): { id: string; clip: boolean } | null {
  const n = node as { id?: string; type?: string; clip?: unknown; fills?: { type?: string; visible?: boolean; resultSet?: { kind?: string } }[] } | null;
  if (!n?.id) return null;
  if (n.type === 'image') return { id: n.id, clip: !!n.clip };
  const fill = n.type === 'rect' ? (n.fills ?? []).find((paint) => paint.type === 'image' && paint.visible !== false) : undefined;
  return fill ? { id: n.id, clip: fill.resultSet?.kind === 'video' } : null;
}

/** The tools Jev may pick from for this picture: its own kind's, never Image to Video (the kind question covers that). */
const toolsFor = (clip: boolean): ToolDef[] => TOOLS.filter((tool) => (clip ? tool.group === 'clip' : tool.group !== 'clip' && tool.id !== 'animate'));

/** One suggestion under the composer. */
export interface Hint { key: string; text: string; action: string; apply: () => void }

/** Acted on as a suggestion: sure, or an offer it is fairly sure of. */
const worth = (d: Decided<unknown> | null) => !!d && (d.band === 'sure' || (d.band === 'offer' && d.confidence >= 0.6));

export function useGenerationSmarts(o: {
  documentId: string;
  readOnly: boolean;
  kind: MediaKind;
  models: Model[];
  modelId: string;
  model: Model | undefined;
  aspect: string;
  prompt: string;
  setModelId: (id: string) => void;
  setAspect: (ratio: string) => void;
  /** The person took the frame their words ask for; `setAspect` is the learned starting frame. */
  pickAspect: (ratio: string) => void;
  /** A reference sets the frame: the learned one stays out of it. */
  holdFrame: boolean;
  changeKind: (kind: MediaKind) => void;
  /** The one picture chosen on the page: then the words may ask to change it with a picture tool instead. */
  picture?: { id: string; clip: boolean } | null;
}): { hint: Hint | null; dismiss: () => void } {
  // ── Learned frame: once per file and kind, over the frame the panel started on. The learned
  // MODEL is not here: it is decided before the list shows (startingModel.ts), so it never swaps.
  const opened = useRef<Record<string, { aspect?: string } | undefined>>({});
  const asked = useRef<Set<string>>(new Set());
  const [pendingAspect, setPendingAspect] = useState<string | null>(null);
  const scope = `${o.documentId}:${o.kind}`;
  useEffect(() => {
    if (o.readOnly || !o.models.length || !o.modelId) return;
    opened.current[scope] ??= {};
    if (asked.current.has(scope)) return;
    asked.current.add(scope);
    const kind = o.kind;
    askLearned(o.documentId, kind).then((learned) => {
      if (learned?.aspect) setPendingAspect(learned.aspect);
      else if (!learned) asked.current.delete(`${o.documentId}:${kind}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, o.models.length, o.modelId, o.readOnly]);
  // The frame waits for the model's own frames (the panel clamps the frame when a model loads),
  // and is only set over the frame the panel started on.
  useEffect(() => {
    if (!pendingAspect || !o.model?.ratios.length) return;
    const start = opened.current[scope];
    if (start && start.aspect === undefined) start.aspect = o.aspect;
    if (!o.holdFrame && o.model.ratios.includes(pendingAspect) && (!start || start.aspect === o.aspect || o.aspect === o.model.ratios[0])) o.setAspect(pendingAspect);
    setPendingAspect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAspect, o.model]);

  // ── Understanding the words: asked 700 ms after typing stops.
  const words = plain(o.prompt);
  const [understood, setUnderstood] = useState<{ words: string; answer: Understood } | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  useEffect(() => {
    if (o.readOnly || words.length < 8) return;
    let live = true;
    const timer = setTimeout(() => {
      artworkCmd<Understood>('artwork.ai.understand', {
        surface: 'generate',
        documentId: o.documentId,
        prompt: words.slice(0, 4000),
        kind: o.kind,
        models: o.models.slice(0, 80).map((m) => ({ id: m.id, name: m.name })),
        ...(o.model?.ratios.length ? { ratios: o.model.ratios.slice(0, 24) } : {}),
        ...(o.picture ? { tools: toolsFor(o.picture.clip).map((tool) => ({ id: tool.id, name: `${tool.label} — ${tool.line}` })) } : {}),
      })
        .then((answer) => live && answer && setUnderstood({ words, answer }))
        .catch(() => {});
    }, 700);
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // The list itself is not a reason to ask again; the words, the kind and the model are.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words, o.kind, o.modelId, o.documentId, o.readOnly, o.picture?.id, o.picture?.clip]);

  let hint: Hint | null = null;
  const a = understood && understood.words === words ? understood.answer : null;
  if (a) {
    const named = (id: string) => o.models.find((m) => m.id === id)?.name ?? id;
    const tool = a.tool && o.picture ? toolsFor(o.picture.clip).find((t) => t.id === a.tool!.value) : undefined;
    if (tool && o.picture && worth(a.tool)) {
      // The words ask to change the chosen picture, not to make a new one: its tool, with the words for one that takes them.
      const at = o.picture.id;
      hint = { key: `tool:${tool.id}:${at}`, text: `Your words ask to change this ${o.picture.clip ? 'clip' : 'picture'}.`, action: `Use ${tool.label}`, apply: () => requestPictureTool({ id: at, tool: tool.id, ...(tool.asks?.includes('prompt') ? { words } : {}) }) };
    } else if (worth(a.kind) && a.kind!.value !== o.kind) {
      const to = a.kind!.value;
      hint = { key: `kind:${to}`, text: to === 'video' ? 'Your words ask for a video.' : 'Your words ask for a still picture.', action: to === 'video' ? 'Make a video' : 'Make a picture', apply: () => o.changeKind(to) };
    } else if (worth(a.model) && a.model!.value !== o.modelId) {
      const id = a.model!.value;
      hint = { key: `model:${id}`, text: `You named ${named(id)}.`, action: `Use ${named(id)}`, apply: () => o.setModelId(id) };
    } else if (worth(a.shape) && a.shape!.value !== o.aspect && o.model?.ratios.includes(a.shape!.value)) {
      const ratio = a.shape!.value;
      hint = { key: `shape:${ratio}`, text: `Your words ask for ${ratio}.`, action: `Use ${ratio}`, apply: () => o.pickAspect(ratio) };
    } else if (o.kind === 'image' && worth(a.lettering) && !goodAtLettering(o.modelId)) {
      const better = LETTERING.map((re) => o.models.find((m) => re.test(m.id))).find(Boolean);
      if (better) hint = { key: `lettering:${better.id}`, text: 'Your picture has words in it.', action: `Use ${better.name} for lettering`, apply: () => o.setModelId(better.id) };
    }
  }
  if (hint && dismissed === `${words}|${hint.key}`) hint = null;
  return { hint, dismiss: () => hint && setDismissed(`${words}|${hint.key}`) };
}

/** Recently told, so one choice is never sent twice in a burst (a double press, a re-render). */
const told = new Map<string, number>();

/**
 * Tells Taste about a choice made on the canvas about generated pictures. `assetIds` are the
 * FILE's asset ids (a fill's `assetId`, a result set's `assetIds`); only those that are a media
 * asset are sent. Fire and forget.
 */
export function reportTaste(
  open: { documentId: string; root: Parameters<typeof assetsMap>[0] },
  kind: 'picked' | 'reused' | 'regenerated' | 'deleted' | 'referenced',
  assetIds: readonly (string | undefined)[],
  extra: { ageS?: number; media?: readonly string[] } = {},
): void {
  try {
    const map = assetsMap(open.root);
    const media = [
      ...new Set([
        ...assetIds.flatMap((id) => {
          const m = id ? (map.get(id) as { mediaAssetId?: string } | undefined)?.mediaAssetId : undefined;
          return m ? [m] : [];
        }),
        ...(extra.media ?? []),
      ]),
    ].slice(0, 16);
    if (!media.length) return;
    const key = `${kind}:${media.join(',')}`;
    const now = Date.now();
    if ((told.get(key) ?? 0) > now - 10_000) return;
    told.set(key, now);
    void artworkCmd('artwork.ai.taste', { documentId: open.documentId, kind, mediaAssetIds: media, ...(extra.ageS !== undefined ? { ageS: Math.max(0, Math.round(extra.ageS)) } : {}) }).catch(() => {});
  } catch {
    // Taste is never in the way of an edit.
  }
}

/** What a shape on the page shows, as media assets: its picture, its fills' pictures and every take in a result set. */
export function shownMedia(node: unknown, root: Parameters<typeof assetsMap>[0]): Set<string> {
  const n = node as { assetId?: string; fills?: { assetId?: string; resultSet?: { assetIds?: string[] } }[] } | null;
  const ids = [n?.assetId, ...(n?.fills ?? []).flatMap((paint) => [paint.assetId, ...(paint.resultSet?.assetIds ?? [])])].filter((id): id is string => !!id);
  const map = assetsMap(root);
  return new Set(ids.flatMap((id) => {
    const m = (map.get(id) as { mediaAssetId?: string } | undefined)?.mediaAssetId;
    return m ? [m] : [];
  }));
}

interface LandedRun { key: string; targetId: string; applied: boolean; createdAt: number; results: { mediaAssetId: string }[] }
interface UndoEvents { on(name: 'stack-item-popped', fn: (event: { type: string }) => void): void; off(name: 'stack-item-popped', fn: (event: { type: string }) => void): void }

/**
 * The landed runs whose result is no longer on the page: the frame is gone, or (after an undo) the
 * shape no longer shows any of the run's pictures. Runs already told are left out.
 */
export function takenBack<R extends LandedRun>(
  runs: readonly R[],
  page: { root: Parameters<typeof assetsMap>[0]; scene: { get(id: string): unknown } },
  told: ReadonlySet<string>,
  afterUndo: boolean,
): R[] {
  return runs.filter((run) => {
    if (!run.applied || !run.results.length || told.has(run.key)) return false;
    const node = page.scene.get(run.targetId);
    if (!node) return true;
    if (!afterUndo) return false;
    const shown = shownMedia(node, page.root);
    return !run.results.some((result) => shown.has(result.mediaAssetId));
  });
}

/**
 * Taste hears when a generated result is taken back off the page: its frame deleted (or its creation
 * undone), or an undo that puts the shape's fill back as it was before the result landed. Either is
 * `deleted` with the result's age — Atlas counts a fresh one as a "no" and an old clean-up as nothing.
 * Once per run; a redo does not take it back.
 */
export function useResultTaste(
  open: { documentId: string; root: Parameters<typeof assetsMap>[0]; scene: { get(id: string): unknown }; undo: UndoEvents },
  runs: readonly LandedRun[],
): void {
  const told = useRef(new Set<string>());
  const latest = useRef({ runs, open });
  latest.current = { runs, open };
  const check = useRef((afterUndo: boolean) => {
    const { runs: now, open: page } = latest.current;
    for (const run of takenBack(now, page, told.current, afterUndo)) {
      told.current.add(run.key);
      reportTaste(page, 'deleted', [], { media: run.results.map((result) => result.mediaAssetId), ageS: (Date.now() - run.createdAt) / 1000 });
    }
  });
  // A frame gone: seen on any render.
  useEffect(() => check.current(false));
  // An undo that took a result off its shape: checked once the undo has landed.
  useEffect(() => {
    const popped = (event: { type: string }) => {
      if (event.type === 'undo') setTimeout(() => check.current(true), 0);
    };
    open.undo.on('stack-item-popped', popped);
    return () => open.undo.off('stack-item-popped', popped);
  }, [open.undo]);
}
